/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Select, SelectChangeEvent } from '@mui/material';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { Labels } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import { SelectInput } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';

interface ActionDropdownProps {
  options: { value: string; label: string; onClick: () => void }[];
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ options }) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { t } = useTranslation(['jobManagement']);

  const handleValueChange = (event: SelectChangeEvent<string>) => {
    const selectedOption = event && event.target ? event.target.value : null;
    setSelectedValue(selectedOption);
    const action = selectedOption ? options.find((option) => option.value === selectedOption) : null;
    if (action) {
      action.onClick();
      setSelectedValue(null);
      setIsDropdownOpen(false);
    }
  };

  const handleIconClick = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  return (
    <Box>
      <Select
        value={selectedValue || ''}
        onChange={handleValueChange}
        open={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
        onOpen={() => setIsDropdownOpen(true)}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) return t('jobManagement:selectAction');
          const selectedOption = options.find((option) => option.value === selected);
          return selectedOption ? selectedOption.label : t('jobManagement:selectAction');
        }}
        input={
          <SelectInput
            sx={{
              '& .MuiSelect-select': { width: '6.25rem' },
            }}
          />
        }
        IconComponent={() => (
          <img
            src={Icons.DropdownIcon}
            alt="dropdown-icon"
            onClick={handleIconClick}
            style={{
              transform: isDropdownOpen ? Labels.Rotate : 'none',
              transition: 'transform 0.3s',
              position: 'absolute',
              right: '0.75rem',
              cursor: 'pointer',
            }}
          />
        )}>
        {options.map((option) => (
          <Options key={option.value} value={option.value}>
            {option.label}
          </Options>
        ))}
      </Select>
    </Box>
  );
};

export default ActionDropdown;
